import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  TruckIcon,
  UserIcon,
  MapPinIcon,
  CubeIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  PhoneIcon,
  EnvelopeIcon,
  PrinterIcon,
  ShareIcon,
  EyeIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ScaleIcon
} from '@heroicons/react/24/outline'

// Interfaces
interface Envio {
  id: string
  numeroGuia: string
  remitente: {
    nombre: string
    empresa?: string
    telefono: string
    email?: string
    direccion: string
    ciudad: string
    identificacion?: string
  }
  destinatario: {
    nombre: string
    empresa?: string
    telefono: string
    email?: string
    direccion: string
    ciudad: string
    identificacion?: string
    instruccionesEntrega?: string
  }
  paquete: {
    descripcion: string
    peso: number
    dimensiones: {
      largo: number
      ancho: number
      alto: number
    }
    valor?: number
    categoria: string
    fragil: boolean
    requiereRefrigeracion: boolean
  }
  estado: 'pendiente' | 'en_transito' | 'entregado' | 'cancelado' | 'devuelto'
  fechaCreacion: string
  fechaEntregaEstimada: string
  fechaEntregaReal?: string
  costo: number
  metodoPago: string
  observaciones?: string
  conductor?: {
    id: string
    nombre: string
    telefono: string
    email?: string
  }
  vehiculo?: {
    id: string
    placa: string
    tipo: string
    marca?: string
    modelo?: string
  }
  seguimiento: EventoSeguimiento[]
  opciones: {
    seguro: boolean
    confirmacionEntrega: boolean
    notificacionSMS: boolean
    notificacionEmail: boolean
  }
}

interface EventoSeguimiento {
  id: string
  fecha: string
  estado: string
  descripcion: string
  ubicacion?: string
  responsable?: string
  observaciones?: string
}

const PaginaDetalleEnvio: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [envio, setEnvio] = useState<Envio | null>(null)
  const [cargando, setCargando] = useState(true)
  

  // Cargar datos del envío
  useEffect(() => {
    const cargarEnvio = async () => {
      setCargando(true)
      
      // Simular carga de datos
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Datos de ejemplo
      const envioEjemplo: Envio = {
        id: id || '1',
        numeroGuia: 'LXP-2024-001',
        remitente: {
          nombre: 'Juan Pérez',
          empresa: 'Empresa ABC',
          telefono: '+57 300 123 4567',
          email: 'juan.perez@empresa.com',
          direccion: 'Calle 123 #45-67, Chapinero',
          ciudad: 'Bogotá',
          identificacion: '12345678'
        },
        destinatario: {
          nombre: 'María García',
          telefono: '+57 301 234 5678',
          email: 'maria.garcia@email.com',
          direccion: 'Carrera 89 #12-34, El Poblado',
          ciudad: 'Medellín',
          identificacion: '87654321',
          instruccionesEntrega: 'Entregar en recepción del edificio, piso 5'
        },
        paquete: {
          descripcion: 'Documentos importantes y contratos',
          peso: 0.5,
          dimensiones: { largo: 30, ancho: 20, alto: 5 },
          valor: 50000,
          categoria: 'documentos',
          fragil: false,
          requiereRefrigeracion: false
        },
        estado: 'en_transito',
        fechaCreacion: '2024-01-15T10:30:00Z',
        fechaEntregaEstimada: '2024-01-17T16:00:00Z',
        costo: 25000,
        metodoPago: 'Efectivo',
        observaciones: 'Entrega urgente para firma de contratos',
        conductor: {
          id: 'c1',
          nombre: 'Carlos Rodríguez',
          telefono: '+57 302 345 6789',
          email: 'carlos.rodriguez@logixport.com'
        },
        vehiculo: {
          id: 'v1',
          placa: 'ABC-123',
          tipo: 'Motocicleta',
          marca: 'Honda',
          modelo: 'CB 190R'
        },
        opciones: {
          seguro: true,
          confirmacionEntrega: true,
          notificacionSMS: true,
          notificacionEmail: true
        },
        seguimiento: [
          {
            id: '1',
            fecha: '2024-01-15T10:30:00Z',
            estado: 'creado',
            descripcion: 'Envío creado y registrado en el sistema',
            ubicacion: 'Centro de distribución Bogotá',
            responsable: 'Sistema LogiXPort'
          },
          {
            id: '2',
            fecha: '2024-01-15T14:15:00Z',
            estado: 'recolectado',
            descripcion: 'Paquete recolectado del remitente',
            ubicacion: 'Calle 123 #45-67, Chapinero, Bogotá',
            responsable: 'Carlos Rodríguez',
            observaciones: 'Recolección exitosa, paquete en perfecto estado'
          },
          {
            id: '3',
            fecha: '2024-01-15T16:45:00Z',
            estado: 'en_centro',
            descripcion: 'Paquete llegó al centro de distribución',
            ubicacion: 'Centro de distribución Bogotá',
            responsable: 'Ana López'
          },
          {
            id: '4',
            fecha: '2024-01-16T08:30:00Z',
            estado: 'en_transito',
            descripcion: 'Paquete en ruta hacia destino',
            ubicacion: 'En ruta Bogotá - Medellín',
            responsable: 'Carlos Rodríguez',
            observaciones: 'Tiempo estimado de llegada: 17 de enero, 4:00 PM'
          }
        ]
      }
      
      setEnvio(envioEjemplo)
      setCargando(false)
    }

    cargarEnvio()
  }, [id])

  // Obtener color del estado
  const obtenerColorEstado = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'en_transito':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'entregado':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'cancelado':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'devuelto':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  // Obtener icono del estado
  const obtenerIconoEstado = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return ClockIcon
      case 'en_transito':
        return TruckIcon
      case 'entregado':
        return CheckCircleIcon
      case 'cancelado':
        return XCircleIcon
      case 'devuelto':
        return ArrowPathIcon
      default:
        return ClockIcon
    }
  }

  // Formatear fecha
  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Formatear fecha corta
  const formatearFechaCorta = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-CO', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Formatear moneda
  const formatearMoneda = (valor: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor)
  }

  // Imprimir guía
  const imprimirGuia = () => {
    window.print()
  }

  // Compartir envío
  const compartirEnvio = () => {
    if (navigator.share && envio) {
      navigator.share({
        title: `Envío ${envio.numeroGuia}`,
        text: `Seguimiento del envío ${envio.numeroGuia}`,
        url: window.location.href
      })
    } else {
      // Fallback: copiar al portapapeles
      navigator.clipboard.writeText(window.location.href)
      alert('Enlace copiado al portapapeles')
    }
  }

  if (cargando) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"
        />
      </div>
    )
  }

  if (!envio) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Envío no encontrado
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            El envío que buscas no existe o ha sido eliminado.
          </p>
          <button
            onClick={() => navigate('/dashboard/envios')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Volver a Envíos
          </button>
        </div>
      </div>
    )
  }

  const IconoEstado = obtenerIconoEstado(envio.estado)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/dashboard/envios')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Volver a Envíos
          </button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
                <CubeIcon className="h-8 w-8 text-blue-500 mr-3" />
                {envio.numeroGuia}
              </h1>
              <div className="mt-2 flex items-center space-x-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${obtenerColorEstado(envio.estado)}`}>
                  <IconoEstado className="h-4 w-4 mr-1" />
                  {envio.estado.replace('_', ' ').toUpperCase()}
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  Creado el {formatearFechaCorta(envio.fechaCreacion)}
                </span>
              </div>
            </div>
            
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button
                onClick={imprimirGuia}
                className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <PrinterIcon className="h-4 w-4 mr-2" />
                Imprimir
              </button>
              <button
                onClick={compartirEnvio}
                className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ShareIcon className="h-4 w-4 mr-2" />
                Compartir
              </button>
              <button
                onClick={() => navigate(`/dashboard/envios/${envio.id}/seguimiento`)}
                className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                Seguimiento
              </button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Información principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Información del envío */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
                <DocumentTextIcon className="h-6 w-6 text-blue-500 mr-2" />
                Información del Envío
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                    <UserIcon className="h-5 w-5 text-gray-500 mr-2" />
                    Remitente
                  </h3>
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                    <p><strong>Nombre:</strong> {envio.remitente.nombre}</p>
                    {envio.remitente.empresa && (
                      <p><strong>Empresa:</strong> {envio.remitente.empresa}</p>
                    )}
                    <p><strong>Teléfono:</strong> {envio.remitente.telefono}</p>
                    {envio.remitente.email && (
                      <p><strong>Email:</strong> {envio.remitente.email}</p>
                    )}
                    <p><strong>Dirección:</strong> {envio.remitente.direccion}</p>
                    <p><strong>Ciudad:</strong> {envio.remitente.ciudad}</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                    <MapPinIcon className="h-5 w-5 text-gray-500 mr-2" />
                    Destinatario
                  </h3>
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                    <p><strong>Nombre:</strong> {envio.destinatario.nombre}</p>
                    {envio.destinatario.empresa && (
                      <p><strong>Empresa:</strong> {envio.destinatario.empresa}</p>
                    )}
                    <p><strong>Teléfono:</strong> {envio.destinatario.telefono}</p>
                    {envio.destinatario.email && (
                      <p><strong>Email:</strong> {envio.destinatario.email}</p>
                    )}
                    <p><strong>Dirección:</strong> {envio.destinatario.direccion}</p>
                    <p><strong>Ciudad:</strong> {envio.destinatario.ciudad}</p>
                    {envio.destinatario.instruccionesEntrega && (
                      <p><strong>Instrucciones:</strong> {envio.destinatario.instruccionesEntrega}</p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Información del paquete */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
                <CubeIcon className="h-6 w-6 text-blue-500 mr-2" />
                Información del Paquete
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Descripción</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{envio.paquete.descripcion}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <ScaleIcon className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Peso</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{envio.paquete.peso} kg</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <CubeIcon className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Dimensiones</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {envio.paquete.dimensiones.largo} x {envio.paquete.dimensiones.ancho} x {envio.paquete.dimensiones.alto} cm
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Categoría</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100 capitalize">{envio.paquete.categoria}</p>
                    </div>
                  </div>
                  
                  {envio.paquete.valor && (
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Valor declarado</p>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{formatearMoneda(envio.paquete.valor)}</p>
                      </div>
                    </div>
                  )}
                  
                  {(envio.paquete.fragil || envio.paquete.requiereRefrigeracion) && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Características especiales</p>
                      <div className="flex flex-wrap gap-2">
                        {envio.paquete.fragil && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            Frágil
                          </span>
                        )}
                        {envio.paquete.requiereRefrigeracion && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            Refrigeración
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Timeline de seguimiento */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
                <ClockIcon className="h-6 w-6 text-blue-500 mr-2" />
                Historial de Seguimiento
              </h2>
              
              <div className="space-y-4">
                {envio.seguimiento.map((evento, index) => (
                  <motion.div
                    key={evento.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start space-x-4"
                  >
                    <div className={`flex-shrink-0 w-3 h-3 rounded-full mt-2 ${
                      index === 0 ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {evento.descripcion}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {formatearFechaCorta(evento.fecha)}
                        </p>
                      </div>
                      {evento.ubicacion && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                          📍 {evento.ubicacion}
                        </p>
                      )}
                      {evento.responsable && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          👤 {evento.responsable}
                        </p>
                      )}
                      {evento.observaciones && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 italic">
                          {evento.observaciones}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Información de entrega */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <CalendarIcon className="h-5 w-5 text-blue-500 mr-2" />
                Información de Entrega
              </h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Fecha estimada</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {formatearFecha(envio.fechaEntregaEstimada)}
                  </p>
                </div>
                
                {envio.fechaEntregaReal && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Fecha real</p>
                    <p className="font-medium text-green-600 dark:text-green-400">
                      {formatearFecha(envio.fechaEntregaReal)}
                    </p>
                  </div>
                )}
                
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Costo</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {formatearMoneda(envio.costo)}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Método de pago</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                    {envio.metodoPago}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Conductor y vehículo */}
            {envio.conductor && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <TruckIcon className="h-5 w-5 text-blue-500 mr-2" />
                  Conductor y Vehículo
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Conductor</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {envio.conductor.nombre}
                    </p>
                    <div className="flex items-center space-x-3 mt-2">
                      <a
                        href={`tel:${envio.conductor.telefono}`}
                        className="flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <PhoneIcon className="h-4 w-4 mr-1" />
                        {envio.conductor.telefono}
                      </a>
                      {envio.conductor.email && (
                        <a
                          href={`mailto:${envio.conductor.email}`}
                          className="flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <EnvelopeIcon className="h-4 w-4 mr-1" />
                          Email
                        </a>
                      )}
                    </div>
                  </div>
                  
                  {envio.vehiculo && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Vehículo</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {envio.vehiculo.tipo} - {envio.vehiculo.placa}
                      </p>
                      {envio.vehiculo.marca && envio.vehiculo.modelo && (
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {envio.vehiculo.marca} {envio.vehiculo.modelo}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Opciones del envío */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Servicios Incluidos
              </h3>
              
              <div className="space-y-2">
                {envio.opciones.seguro && (
                  <div className="flex items-center text-green-600 dark:text-green-400">
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm">Seguro de mercancía</span>
                  </div>
                )}
                {envio.opciones.confirmacionEntrega && (
                  <div className="flex items-center text-green-600 dark:text-green-400">
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm">Confirmación con foto</span>
                  </div>
                )}
                {envio.opciones.notificacionSMS && (
                  <div className="flex items-center text-green-600 dark:text-green-400">
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm">Notificaciones SMS</span>
                  </div>
                )}
                {envio.opciones.notificacionEmail && (
                  <div className="flex items-center text-green-600 dark:text-green-400">
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm">Notificaciones Email</span>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Observaciones */}
            {envio.observaciones && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Observaciones
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  {envio.observaciones}
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaginaDetalleEnvio
